<template>
  <VcLayout column>
    <VcLayout :class="{'multi-staff-handler' : !hideMultiStaff}">
      <component :is="inputComponent"
                 v-bind="$attrs"
                 :value="primeStaff"
                 v-on="$listeners"
                 :class="{'multi-staff' : !hideMultiStaff}"
                 :filter="filterItems"
                 :items="staffs"
                 :label="$attrs.value ? $t('settings.service_page.title.staff') : $t('staff.client_assign.dialog.select_placeholder')"
                 :menuProps="{contentClass: 'staff-selection-menu-content'}"
                 :use-native-select="false"
                 class="staff-selection"
                 hide-no-data
                 item-text="full_name"
                 item-value="uid"
                 return-object>
        <template v-slot:selection="{item, attrs, on}">
          <v-layout class="staff-selection-item" column>

            <v-list-item
              :key="item.uid"
              v-bind="attrs"
              v-on="on"
              class="selection-list-item">
              <AvatarImage :colorId="item.color_id" :imagePath="item.photo_path" :name="item.full_name"
                           class="staff-avatar selection" customSize="24px"></AvatarImage>

              <v-list-item-content class="item-content">
                <v-list-item-title class="staff-name">
                  {{ item === currStaff ? item.full_name + ' ' + $t('staff.staff_selection.me') : item.full_name }}

                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-layout>

        </template>
        <template v-slot:item="{item, attrs, on}">
          <v-list-item
            :key="item.uid"
            v-bind="attrs"
            v-on="on"
            :disabled="isDisabled(item.uid)"
            class="staff-list-item">
            <v-list-item-avatar class="staff-image-container">
              <AvatarImage :colorId="item.color_id" :imagePath="item.photo_path" :name="item.full_name"
                           class="staff-avatar"
                           customSize="24px"></AvatarImage>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="staff-name">
                {{ item === currStaff ? item.full_name + ' ' + $t('staff.staff_selection.me') : item.full_name }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </component>
      <VcTooltip v-if="!hideMultiStaff"
                 :content="$t('staff.staff_selection.additional_staff')"
                 :disabled="!$vuetify.breakpoint.mdAndUp"
                 dark bottom>
        <template>
          <additional-staff-popover
            :additional-staffs="additionalStaffs"
            :current-staff-uid="primeStaff.uid"
            :staffs="staffs"
            @submit="onSelection">
          </additional-staff-popover>
        </template>
      </VcTooltip>
    </VcLayout>
    <VcLayout v-if="currentAdditionalStaffs.length" column class="additional-chips">
      <span class="chips-title">{{ $t('staff.staff_selection.additional_staff_chips_title') }}</span>
      <VcLayout class="wrap">
        <VcChip v-for="(staff, idx) in currentAdditionalStaffs"
                v-if="staff.checked"
                class="staff-chip"
                v-model="staff.checked"
                :key="idx"
                :label="staff.label"
                :avatar="staff.avatar"
                :invalid="isAvailable(staff.uid)"
                :data-qa="`staff-chip-${idx}`"
                @input="removeStaff(staff)">
        </VcChip>
      </VcLayout>
    </VcLayout>
  </VcLayout>
</template>`

<script>
import {AvatarImage} from "@/modules/common/components";
import {mapGetters} from "vuex";
import AdditionalStaffPopover from "@/modules/bookings/components/AdditionalStaffDialog/components/additionalStaffPopover";

export default {
  components: {
    AdditionalStaffPopover,
    AvatarImage
  },
  props: {
    primeStaff: {
      type: Object,
      required: true,
      default: () => ({}),
      validator: prop => prop.uid,
    },
    staffs: {
      type: Array,
      required: true,
    },
    additionalStaffs: {
      type: Array,
      default: () => ([])
    },
    currentStaffUid: {
      type: String,
      default: ''
    },
    hideMultiStaff: {
      type: Boolean,
      default: false
    },
    additionalStaffAvailability: {
      type: Object
    }
  },
  model: {
    prop: 'primeStaff'
  },
  data: () => ({
    currentAdditionalStaffs: [],
    additionalStaffsUids: [],
  }),
  mounted() {
    if (this.additionalStaffs) {
      this.currentAdditionalStaffs = this.staffs.flatMap(staff => {
        if (this.additionalStaffs.some((sUid) => sUid === staff.uid)) {
          return {
            ...this.getStaffInfo(staff),
            checked: true
          }
        }
        return []
      })
    }
  },
  methods: {
    filterItems(item, queryText, itemText) {
      if (item.header || typeof item === 'string') {
        return true
      }
      return itemText.toLowerCase().indexOf(queryText.toLowerCase()) !== -1
    },
    onSelection(selections) {
      this.additionalStaffsUids = [...selections]
      this.submitSelection()
    },
    submitSelection() {
      this.currentAdditionalStaffs = this.staffs.flatMap(staff => {
        if (this.additionalStaffsUids.some((sUid) => sUid === staff.uid)) {
          return {
            ...this.getStaffInfo(staff),
            checked: true
          }
        }
        return []
      })

      this.$emit('submitAdditionalStaff', this.additionalStaffsUids)
    },
    getStaffInfo(staff) {
      return {
        color_id: staff.color_id,
        display_name: staff.display_name,
        email: staff.email,
        full_name: staff.full_name,
        photo_path: staff.photo_path,
        role: staff.role,
        avatar: staff.avatar,
        label: staff.label,
        uid: staff.uid,
      }
    },
    removeStaff(staff) {
      this.additionalStaffsUids = this.additionalStaffsUids.filter((sUid) => sUid !== staff.uid)
      this.submitSelection()
    },
    isDisabled(uid) {
      return this.currentAdditionalStaffs.some(staff => staff.uid === uid)
    },
    isAvailable(staffUid) {
      return this.additionalStaffAvailability[staffUid]
    }
  },
  computed: {
    ...mapGetters('accountModule', ['staff']),
    currStaff() {
      return this.staffs.find(staff => staff.uid === this.currentStaffUid)
    },
    inputComponent() {
      return this.$vuetify.breakpoint.mdAndUp ? () => import("@/modules/common/components/VcitaAutoComplete/VcitaAutoComplete.vue") : () => import("@/modules/common/components/VcitaSelectDropDown/VcitaSelectDropDown.vue");
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_i18n-mixins.scss";

.multi-staff-handler {
  border: 1px solid #E0E0E0;
  border-radius: 6px;

  .empty-state {
    margin-block: 40px 4px;
  }
}

.additional-chips {
  .chips-title {
    font-weight: 600;
    font-size: 10px;
    line-height: 14px;
    letter-spacing: 0.03em;
    color: #999999;
    margin-block: 8px 4px;
  }

  .staff-chip {
    margin-inline-end: 6px;
    margin-block-end: 6px;
  }
}

.description-line {
  font-size: 12px;
  letter-spacing: 0.02em;
  margin-block-start: 20px;
  color: #212121;
}

.staff-image-container.v-avatar.v-list-item__avatar {
  @include i-margin-mirror(0, 8px);
  max-height: 24px;
  max-width: 24px;
  min-width: 24px !important;

  .staff-image {
    border-radius: 1000px;
  }

}

.staff-list-item {
  max-height: 40px;
  min-height: 40px;

  &.primary--text {
    background-color: rgba(0, 0, 0, 0.03);
    color: var(--brand-color-secondary) !important;

    .staff-name {
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }

  &.v-list-item--disabled {
    .staff-image-container {
      filter: grayscale(100%);
    }
  }
}

.staff-name {
  font-family: var(--primary-font-family);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.2px;
  text-transform: none;
}

.staff-avatar {
  font-family: var(--primary-font-family);
  font-style: normal;
  font-weight: 500;
  font-size: 9px;
  line-height: 12px;

  &.selection {
    @include i-margin-mirror(0, 8px);
  }
}

.staff-selection-item {
  max-width: 100%;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: initial;
}

.selection-title {
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.3px;
  color: rgba(0, 0, 0, 0.62);
}

.item-content {
  padding: 0;
}

.selection-list-item {
  min-height: revert;
  padding: 0;

  .staff-name {
    font-family: var(--primary-font-family);
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.2px;
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_i18n-mixins.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.staff-selection-menu-content {
  font-family: var(--primary-font-family);
  @include md-and-up {
    max-width: 412px;
  }
}

.staff-selection {
  max-height: 52px;
  background: #fff;

  .v-input__control {
    max-height: 52px;

    .v-select__slot {
      .v-label {
        color: rgba(0, 0, 0, 0.6) !important;
      }
    }
  }

  .v-input__slot {
    min-height: 52px !important;
    align-items: flex-end;
  }

  .v-avatar {
    margin: 0;
  }

  .v-input__append-inner {
    min-height: 52px;
    position: absolute;
    @include i-position-mirror(right, 0);
    bottom: 0;

    .v-input__icon {
      margin: auto;
    }
  }

  &.error--text {
    .v-input__control {
      .v-input__slot {
        border: 1px solid #F2514A;
      }
    }
  }

  &.multi-staff {
    .v-input__control {
      .v-input__slot, .v-input__slot:hover {
        border: none;
        border-right: 1px solid rgba(0, 0, 0, 0.12);
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
}
</style>
